import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiDownload,
  FiFilter,
  FiSearch,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from '../../components/common/ClientPagination';

const number = (value) => Number(value || 0);

const quantity = (value) =>
  number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

const movementLabel = (type) => {
  const labels = {
    OPENING_BALANCE: "Opening Stock",
    ADJUSTMENT_IN: "Adjustment In",
    ADJUSTMENT_OUT: "Adjustment Out",
    STOCK_COUNT_GAIN: "Stock Count Gain",
    STOCK_COUNT_LOSS: "Stock Count Loss",
    TRANSFER_IN: "Transfer In",
    TRANSFER_OUT: "Transfer Out",
    PURCHASE_RECEIPT: "Purchase Receipt",
    SALE: "Sale",
    SALE_RETURN: "Sale Return",
  };

  return labels[type] || (type || "Movement").replaceAll("_", " ");
};

const movementStyle = (type, quantityChange) => {
  if ((type || "").includes("TRANSFER")) {
    return "bg-blue-100 text-blue-700";
  }

  if (number(quantityChange) > 0) {
    return "bg-green-100 text-green-700";
  }

  if (number(quantityChange) < 0) {
    return "bg-red-100 text-red-700";
  }

  return "bg-orange-100 text-orange-700";
};

const date = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const ledgerKey = (movement) =>
  `${movement.variantId}-${movement.storeId}`;

export default function StockLedgerPage() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [movements, setMovements] = useState([]);
  const [balances, setBalances] = useState([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [storeId, setStoreId] = useState("");
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [extraFilters, setExtraFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadLedger = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const [movementsData, balancesData] = await Promise.all([
        apiRequest("/inventory/movements", { accessToken }),
        apiRequest("/inventory/balances", { accessToken }),
      ]);

      setMovements(movementsData || []);
      setBalances(balancesData || []);
    } catch (err) {
      setError(err.message || "Unable to load stock ledger.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadLedger();
  }, [isAuthLoading, accessToken, loadLedger]);

  const movementsWithBalance = useMemo(() => {
    const runningBalances = new Map();

    // Calculate in chronological order, then return newest first.
    return [...movements]
      .sort(
        (first, second) =>
          new Date(first.occurredAt).getTime() -
          new Date(second.occurredAt).getTime(),
      )
      .map((movement) => {
        const key = ledgerKey(movement);

        const nextBalance =
          (runningBalances.get(key) || 0) + number(movement.quantityChange);

        runningBalances.set(key, nextBalance);

        return {
          ...movement,
          runningBalance: nextBalance,
        };
      })
      .sort(
        (first, second) =>
          new Date(second.occurredAt).getTime() -
          new Date(first.occurredAt).getTime(),
      );
  }, [movements]);

  const stores = useMemo(() => {
    const uniqueStores = new Map();

    movements.forEach((movement) => {
      if (movement.store) {
        uniqueStores.set(movement.store.id, movement.store);
      }
    });

    return [...uniqueStores.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [movements]);

  const movementTypes = useMemo(
    () => [...new Set(movements.map((movement) => movement.type))].sort(),
    [movements],
  );

  const filteredMovements = useMemo(() => {
    const term = search.trim().toLowerCase();

    return movementsWithBalance.filter((movement) => {
      const productName =
        movement.variant?.product?.name?.toLowerCase() || "";

      const sku = movement.variant?.sku?.toLowerCase() || "";

      const reference = `${movement.referenceType || ""} ${
        movement.referenceId || ""
      }`.toLowerCase();

      const matchesSearch =
        !term ||
        productName.includes(term) ||
        sku.includes(term) ||
        reference.includes(term);

      return (
        matchesSearch &&
        (!from || new Date(movement.occurredAt) >= new Date(from + 'T00:00:00+05:30')) &&
        (!to || new Date(movement.occurredAt) <= new Date(to + 'T23:59:59.999+05:30')) &&
        Object.entries(extraFilters).every(([key,value]) => !value || (key === 'productId' ? movement.variant?.product?.id : key === 'colorId' || key === 'sizeId' ? movement.variant?.[key] : movement.variant?.product?.[key]) === value) &&
        (!type || movement.type === type) &&
        (!storeId || movement.storeId === storeId)
      );
    });
  }, [movementsWithBalance, search, type, storeId, from, to, extraFilters]);
  const pagination = useClientPagination(filteredMovements);
  useEffect(() => { pagination.setPage(1); }, [search, type, storeId, from, to, extraFilters]);

  const summary = useMemo(() => {
    const openingStock = movements
      .filter((movement) => movement.type === "OPENING_BALANCE")
      .reduce(
        (total, movement) => total + number(movement.quantityChange),
        0,
      );

    const totalInward = movements
      .filter(
        (movement) =>
          movement.type !== "OPENING_BALANCE" &&
          number(movement.quantityChange) > 0,
      )
      .reduce(
        (total, movement) => total + number(movement.quantityChange),
        0,
      );

    const totalOutward = movements
      .filter((movement) => number(movement.quantityChange) < 0)
      .reduce(
        (total, movement) =>
          total + Math.abs(number(movement.quantityChange)),
        0,
      );

    const closingStock = balances.reduce(
      (total, balance) => total + number(balance.quantityOnHand),
      0,
    );

    return {
      openingStock,
      totalInward,
      totalOutward,
      closingStock,
    };
  }, [balances, movements]);

  const resetFilters = () => {
    setSearch("");
    setType("");
    setStoreId("");
    setFrom('');
    setTo('');
    setExtraFilters({});
  };

  const exportCsv = () => {
    const headers = [
      "Date",
      "Reference",
      "Type",
      "Product",
      "SKU",
      "Store",
      "Inward",
      "Outward",
      "Running Balance",
      "Unit Cost",
      "Total Cost",
      "Note",
    ];

    const rows = filteredMovements.map((movement) => {
      const change = number(movement.quantityChange);

      return [
        date(movement.occurredAt),
        movement.referenceId || movement.id,
        movementLabel(movement.type),
        movement.variant?.product?.name || "",
        movement.variant?.sku || "",
        movement.store?.name || "",
        change > 0 ? change : "",
        change < 0 ? Math.abs(change) : "",
        movement.runningBalance,
        movement.unitCost || "",
        movement.totalCost || "",
        movement.note || "",
      ];
    });

    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `stock-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">Inventory / Ledger</p>

          <h1 className="mt-1 text-3xl font-bold">Stock Ledger</h1>

          <p className="mt-2 text-gray-500">
            Complete immutable inventory movement history.
          </p>
        </div>

        <button
          onClick={exportCsv}
          disabled={filteredMovements.length === 0}
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-700 px-5 text-white disabled:opacity-60"
        >
          <FiDownload />
          Export
        </button>
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <Card
          title="Opening Stock"
          value={loading ? "—" : quantity(summary.openingStock)}
        />

        <Card
          title="Total Inward"
          value={loading ? "—" : quantity(summary.totalInward)}
          color="text-green-600"
        />

        <Card
          title="Total Outward"
          value={loading ? "—" : quantity(summary.totalOutward)}
          color="text-red-600"
        />

        <Card
          title="Closing Stock"
          value={loading ? "—" : quantity(summary.closingStock)}
          color="text-primary-700"
        />
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-5">
        <div className="mb-4 grid gap-3 md:grid-cols-4">
          <label className="text-sm text-gray-600">From date<input type="date" value={from} max={to || undefined} onChange={e=>setFrom(e.target.value)} className="mt-1 h-12 w-full rounded-xl border p-3" /></label>
          <label className="text-sm text-gray-600">To date<input type="date" value={to} min={from || undefined} onChange={e=>setTo(e.target.value)} className="mt-1 h-12 w-full rounded-xl border p-3" /></label>
          { [['productId','Product','product'],['brandId','Brand','brand'],['categoryId','Category','category'],['subCategoryId','Sub category','subCategory'],['seasonId','Season','season'],['colorId','Colour','color'],['sizeId','Size','size']].map(([key,label,relation])=>{
            const options=new Map();
            movements.forEach(m=>{const p=m.variant?.product;const item=relation==='product'?p:relation==='color'||relation==='size'?m.variant?.[relation]:p?.[relation];if(item)options.set(item.id,item);});
            return <label key={key} className="text-sm text-gray-600">{label}<select value={extraFilters[key]||''} onChange={e=>setExtraFilters({...extraFilters,[key]:e.target.value})} className="mt-1 h-12 w-full rounded-xl border px-3"><option value="">All {label}</option>{[...options.values()].sort((a,b)=>a.name.localeCompare(b.name)).map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</select></label>;
          })}
        </div>
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search product, SKU, reference..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Types</option>

            {movementTypes.map((movementType) => (
              <option key={movementType} value={movementType}>
                {movementLabel(movementType)}
              </option>
            ))}
          </select>

          <select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Stores</option>

            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>

          <button
            onClick={resetFilters}
            className="flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 hover:bg-gray-50"
          >
            <FiFilter />
            Reset Filters
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {[
                  "Date",
                  "Reference",
                  "Type",
                  "Product",
                  "Store",
                  "Inward",
                  "Outward",
                  "Balance",
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-6 py-4 text-left text-xs uppercase text-gray-500 last:text-right"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Loading stock ledger…
                  </td>
                </tr>
              ) : filteredMovements.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No ledger movements found.
                  </td>
                </tr>
              ) : (
                pagination.pageItems.map((movement) => {
                  const change = number(movement.quantityChange);
                  const inward = change > 0 ? change : 0;
                  const outward = change < 0 ? Math.abs(change) : 0;

                  return (
                    <tr
                      key={movement.id}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-sm">
                        {date(movement.occurredAt)}
                      </td>

                      <td className="px-6 py-4 text-sm font-medium">
                        {movement.referenceType
                          ? `${movement.referenceType} · `
                          : ""}
                        {movement.referenceId
                          ? movement.referenceId.slice(0, 8)
                          : movement.id.slice(0, 8)}
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-medium ${movementStyle(
                            movement.type,
                            movement.quantityChange,
                          )}`}
                        >
                          {movementLabel(movement.type)}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">
                          {movement.variant?.product?.name ||
                            "Product Variant"}
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          {movement.variant?.sku}
                        </p>
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {movement.store?.name || "—"}
                      </td>

                      <td className="px-6 py-4 text-right font-semibold text-green-600">
                        {inward ? quantity(inward) : "-"}
                      </td>

                      <td className="px-6 py-4 text-right font-semibold text-red-600">
                        {outward ? quantity(outward) : "-"}
                      </td>

                      <td className="px-6 py-4 text-right font-bold">
                        {quantity(movement.runningBalance)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={pagination} label="movements" />
      </div>
    </div>
  );
}

function Card({ title, value, color = "text-gray-900" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{title}</p>

      <h3 className={`mt-2 text-3xl font-bold ${color}`}>{value}</h3>
    </div>
  );
}
